"use client";

import { Button, cn } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  BarChart3,
  List,
  RefreshCw,
  UserRound,
} from "lucide-react";
import type { ShiftReportTotals } from "@/modules/merchant/presentation/lib/shift-report-stats";

export type ShopShiftReportToolbarProps = {
  shopId: string | undefined;
  branchName: string | null;
  weekLabel: string;
  totals: ShiftReportTotals;
  isRefreshing?: boolean;
  onRefresh?: () => void;
};

export function ShopShiftReportToolbar({
  shopId,
  branchName,
  weekLabel,
  totals,
  isRefreshing,
  onRefresh,
}: ShopShiftReportToolbarProps) {
  return (
    <header className="mb-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="border-border border-b bg-muted/25 px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-bold font-heading text-foreground text-xl uppercase tracking-tight sm:text-2xl">
                ລາຍງານກະ
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-2 py-0.5 font-semibold text-[10px] text-sky-900 uppercase tracking-wide dark:text-sky-200">
                <BarChart3 className="size-3" aria-hidden />
                ສະຫຼຸບ
              </span>
              {totals.assignments > 0 ? (
                <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground text-xs tabular-nums">
                  {totals.assignments} ກະ
                </span>
              ) : null}
              {totals.pendingSwaps > 0 ? (
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-medium text-amber-900 text-xs tabular-nums dark:text-amber-200">
                  {totals.pendingSwaps} ຄຳຂໍແລກກະ
                </span>
              ) : null}
            </div>

            <p className="mt-1.5 text-muted-foreground text-sm leading-snug">
              ສະຫຼຸບຕາມວັນ ແລະ ພະນັກງານ — ກະທີ່ບໍ່ຖືກຍົກເລີກ
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              {branchName ? (
                <span className="font-medium text-foreground text-sm">
                  {branchName}
                </span>
              ) : null}
              {branchName ? (
                <span className="text-muted-foreground/50">·</span>
              ) : null}
              <span className="text-muted-foreground tabular-nums">
                {weekLabel}
              </span>
            </div>
          </div>

          {onRefresh ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-1.5 border-border px-3"
              disabled={isRefreshing}
              onClick={onRefresh}
              title="ໂຫຼດຄືນ"
            >
              <RefreshCw
                className={cn("size-4", isRefreshing && "animate-spin")}
                aria-hidden
              />
              <span className="hidden sm:inline">ໂຫຼດຄືນ</span>
            </Button>
          ) : null}
        </div>
      </div>

      {shopId ? (
        <div className="px-4 py-3 sm:px-5">
          <p className="mb-2 font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
            ວຽກດ່ວນ
          </p>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:thin]">
            <Link
              to="/app/shops/$shopId/shifts/calendar"
              params={{ shopId }}
              className={cn(
                "inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 font-medium text-sm transition-colors touch-manipulation",
                "border-violet-500/30 bg-violet-500/10 text-violet-950 hover:bg-violet-500/15 dark:text-violet-100",
              )}
            >
              <List className="size-4 shrink-0" aria-hidden />
              <span>ລາຍການກະ</span>
            </Link>
            <Link
              to="/app/shops/$shopId/shifts/me"
              params={{ shopId }}
              className={cn(
                "inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 font-medium text-sm transition-colors touch-manipulation",
                "border-border bg-muted/30 hover:bg-muted/50",
              )}
            >
              <UserRound className="size-4 shrink-0" aria-hidden />
              <span>ກະຂອງຂ້ອຍ</span>
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}
